/**
 * Recovery from stale lazy-loaded chunks.
 *
 * Every deploy publishes new hashed chunk filenames. A tab that was open across
 * a deploy still holds the old index, so the first lazy import it needs (the map
 * bundle on /products, for example) 404s and throws ChunkLoadError — which in
 * React escapes render and blanks the route. A single reload fetches the current
 * index and fixes it, so do that once, then stop, so a genuinely missing chunk
 * cannot become a reload loop.
 */

export const CHUNK_RELOAD_FLAG = "cheepsweepz_chunk_reload_at";
const RELOAD_COOLDOWN_MS = 60_000;

const CHUNK_ERROR_PATTERNS = [
  /ChunkLoadError/i,
  /Loading chunk .* failed/i,
  /Loading CSS chunk .* failed/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
];

export function isChunkLoadError(error) {
  if (!error) return false;
  const name = typeof error === "object" ? error.name || "" : "";
  const message =
    typeof error === "string" ? error : `${error.message || ""} ${error.toString?.() || ""}`;
  return (
    name === "ChunkLoadError" ||
    CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message))
  );
}

/**
 * Decide whether to reload, given when the last chunk reload happened.
 * Pure so the guard can be tested without touching the browser.
 */
export function shouldReloadForChunkError(error, lastReloadAt, now = Date.now()) {
  if (!isChunkLoadError(error)) return false;
  const previous = Number(lastReloadAt);
  if (Number.isFinite(previous) && previous > 0 && now - previous < RELOAD_COOLDOWN_MS) {
    return false;
  }
  return true;
}

function readFlag() {
  try {
    return window.sessionStorage.getItem(CHUNK_RELOAD_FLAG);
  } catch {
    return null;
  }
}

function writeFlag(value) {
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_FLAG, String(value));
  } catch {
    /* private mode */
  }
}

/**
 * @returns {boolean} true when a reload was triggered.
 */
export function recoverFromChunkError(error) {
  if (typeof window === "undefined") return false;
  if (!shouldReloadForChunkError(error, readFlag())) return false;
  writeFlag(Date.now());
  console.warn("Stale asset detected, reloading to pick up the current build:", error?.message);
  window.location.reload();
  return true;
}

/** Catch chunk failures that happen outside a React render. */
export function installChunkErrorRecovery() {
  if (typeof window === "undefined") return;
  window.addEventListener("error", (event) => {
    recoverFromChunkError(event?.error || event?.message);
  });
  window.addEventListener("unhandledrejection", (event) => {
    recoverFromChunkError(event?.reason);
  });
}
